var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { MessagingChannelFeature } from '@whatsper/texterchat-common';
import { Typography, Input, Button, Spin, Tooltip } from 'antd';
import { CheckOutlined, CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import React, { useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { useStore } from '../../../store/rootStore';
import { formatPhone } from '../../Base/Phone/Formatter';
import HighlightedText from '../../Base/Utils/HighlightedText';
const { Text } = Typography;
const ChatTitleComponent = forwardRef(({ chat, highlighted, maxLength = 40, showId = 'all', className, editable = false, onRename, loading = false, fullNameTooltip = false, }, ref) => {
    const { channels, phoneFormat, defaultCountry } = useStore();
    const [title, setTitle] = useState(chat.title || '---');
    const [originalTitle, setOriginalTitle] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedName, setEditedName] = useState('');
    useImperativeHandle(ref, () => ({
        startEditing: handleStartEditing,
    }));
    useEffect(() => {
        const displayName = chat.displayName;
        let fallbackTitle = '';
        if (chat.title) {
            fallbackTitle = chat.title;
        }
        else if (showId !== 'none') {
            const adapter = channels.adaptersByAccountId[`${chat.channelInfo.name}${chat.channelInfo.accountId}`];
            if (channels.hasFeatures([MessagingChannelFeature.contactPhoneNumberId], adapter)) {
                fallbackTitle = formatPhone(chat.channelInfo.id, phoneFormat, defaultCountry);
            }
            else if (showId === 'all' || channels.hasFeatures([MessagingChannelFeature.contactGlobalId], adapter)) {
                fallbackTitle = chat.channelInfo.id;
            }
        }
        else {
            fallbackTitle = '---';
        }
        if (displayName) {
            setTitle(displayName);
            setOriginalTitle(fallbackTitle);
        }
        else {
            setTitle(fallbackTitle);
            setOriginalTitle(null);
        }
    }, [chat._id, channels.adaptersByAccountId, phoneFormat, defaultCountry, showId, chat.title, chat.displayName]);
    const handleStartEditing = () => {
        if (editable) {
            const currentName = chat.displayName || chat.title || chat.channelInfo.id;
            setEditedName(currentName);
            setIsEditing(true);
        }
    };
    const handleConfirmRename = () => __awaiter(void 0, void 0, void 0, function* () {
        if (onRename) {
            try {
                yield onRename(editedName);
                setIsEditing(false);
            }
            catch (error) {
                console.error('Error renaming chat:', error);
                // Editing mode stays open here
            }
        }
    });
    const handleCancelRename = () => {
        setIsEditing(false);
        setEditedName('');
    };
    if (isEditing && editable) {
        return (React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
            React.createElement(Input, { value: editedName, onChange: (e) => setEditedName(e.target.value), onPressEnter: handleConfirmRename, autoFocus: true, size: "small", style: { maxWidth: '200px' }, disabled: loading }),
            loading ? (React.createElement(Spin, { indicator: React.createElement(LoadingOutlined, { style: { fontSize: 16 }, spin: true }) })) : (React.createElement(React.Fragment, null,
                React.createElement(Button, { type: "text", size: "small", icon: React.createElement(CheckOutlined, { style: { color: 'green' } }), onClick: handleConfirmRename, disabled: loading }),
                React.createElement(Button, { type: "text", size: "small", icon: React.createElement(CloseOutlined, { style: { color: 'red' } }), onClick: handleCancelRename, disabled: loading })))));
    }
    let displayText = title;
    if (originalTitle) {
        displayText = `${title} (${originalTitle})`;
    }
    let cropped = displayText;
    if (!fullNameTooltip && cropped.length > maxLength) {
        cropped = cropped.substring(0, maxLength) + '...';
    }
    const text = (React.createElement(Text, { className: className }, highlighted ? (React.createElement(HighlightedText, { text: cropped, highlighted: highlighted })) : (cropped)));
    return fullNameTooltip ? (React.createElement(Tooltip, { title: displayText, mouseEnterDelay: 0.5 }, text)) : text;
});
/**
 * Show chat title with optional editing capability
 *
 */
export default observer(ChatTitleComponent);
